import * as yup from "yup";

// Same rules as the API's ContactDataRules. Empty is allowed; on edit, a value saved before the rules existed passes
// as long as it is unchanged (form context `saved`, keyed by field name).
type Ctx = { options: { context?: { saved?: Record<string, unknown> } }; path: string };
const unchanged = (value: string | null | undefined, ctx: Ctx) => {
  const saved = ctx.options.context?.saved?.[ctx.path];
  return saved != null && saved !== "" && value === saved;
};
const optional = (test: (v: string) => boolean) => (value: string | null | undefined, ctx: Ctx) =>
  !value || !value.trim() || unchanged(value, ctx) || test(value.trim());

export const optionalEmail = () =>
  yup
    .string()
    .nullable()
    .test(
      "email",
      "Niepoprawny adres email",
      optional((v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)),
    );

export const optionalPhone = () =>
  yup
    .string()
    .nullable()
    .test(
      "phone",
      "Niepoprawny numer telefonu",
      optional((v) => /^\+?[0-9 ()-]{6,20}$/.test(v) && (v.match(/\d/g)?.length ?? 0) >= 6),
    );

export const optionalPostCode = () =>
  yup
    .string()
    .nullable()
    .test(
      "postCode",
      "Kod pocztowy w formacie 00-000",
      optional((v) => /^\d{2}-\d{3}$/.test(v)),
    );

export const optionalCoordinate = (min: number, max: number) =>
  yup
    .string()
    .nullable()
    .test(
      "coordinate",
      `Podaj liczbę od ${min} do ${max}`,
      optional((v) => {
        const n = Number(v.replace(",", "."));
        return v.replace(",", ".").match(/^-?\d+(\.\d+)?$/) !== null && n >= min && n <= max;
      }),
    );

const PESEL_WEIGHTS = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];
const weightedSum = (digits: string, weights: number[]) =>
  weights.reduce((sum, w, i) => sum + w * Number(digits[i]), 0);

export const isValidPesel = (value: string): boolean =>
  /^\d{11}$/.test(value) && (10 - (weightedSum(value, PESEL_WEIGHTS) % 10)) % 10 === Number(value[10]);

// Polish NIP (dashes/spaces and a "PL" prefix allowed) with its check digit, or another EU country's VAT number.
export const isValidNip = (value: string): boolean => {
  const nip = value.toUpperCase().replace(/[\s-]/g, "");
  if (!nip.startsWith("PL") && /^[A-Z]{2}[0-9A-Z]{2,12}$/.test(nip)) return true;
  const digits = nip.startsWith("PL") ? nip.slice(2) : nip;
  return /^\d{10}$/.test(digits) && weightedSum(digits, NIP_WEIGHTS) % 11 === Number(digits[9]);
};

export const optionalPesel = () =>
  yup
    .string()
    .nullable()
    .test("pesel", "Niepoprawny PESEL (11 cyfr z poprawną cyfrą kontrolną)", optional(isValidPesel));

export const optionalNip = () =>
  yup.string().nullable().test("nip", "Niepoprawny NIP (10 cyfr z poprawną cyfrą kontrolną)", optional(isValidNip));
